/* istanbul ignore file */
/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import {
  MessageBar,
  MessageBarType,
  Dialog, DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Spinner,
  SpinnerSize,
} from '@bingads-webui-react/fabric-control';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { Tag } from '@bingads-webui-campaign-react/image-experience';

import CaptionPicker from './caption-picker';
import C from '../../constants';
import { FormatVideoError } from '../../util/format-video-error';
import { CaptionType } from '../../types';

export const useCaptionEditor = (props) => {
  const {
    i18n,
    resourceRepository,
    video,
    activityLogger,
  } = props;
  const [captions, setCaptions] = React.useState([]);
  const [errors, setErrors] = React.useState([]);
  const [block, setBlock] = React.useState(false);

  const updateCaptions = React.useCallback(
    () => resourceRepository
      .getResources({ videoId: video.IdString })
      .then(resources => (resources.value ? resources.value.filter(res => res.Type === 'Caption') : []))
      .then((existedCaptions) => {
        setCaptions(existedCaptions);
      })
      .catch(response => setErrors(FormatVideoError(response, i18n))),
    [video.IdString, resourceRepository, i18n]
  );

  React.useEffect(() => {
    setBlock(true);
    updateCaptions()
      .finally(() => setBlock(false));
  }, [updateCaptions]);

  const component = (
    <CaptionEditor
      i18n={i18n}
      resourceRepository={resourceRepository}
      videoId={video.IdString}
      captions={captions}
      updateCaptions={updateCaptions}
      errors={errors}
      setErrors={setErrors}
      block={block}
      setBlock={setBlock}
      activityLogger={activityLogger}
    />);

  return { component, captions };
};

export const createCaptionFromFile = (file, captions, i18n, setErrors) => {
  const { name } = file;
  const splits = name.split('.');
  const ext = splits.pop().toLowerCase();
  const languageTag = splits.pop();
  const Lcid = C.TagToLcid(languageTag);
  if (!Lcid) {
    setErrors([i18n.getString('ImageExperience_Video_Error_FileNameWrongFormat')]);
    return null;
  }
  if (captions.find(caption => caption.Lcid === Lcid)) {
    setErrors([i18n.getString('ImageExperience_Video_Error_DuplicateVideoResource')]);
    return null;
  }
  return {
    Id: '',
    Name: name,
    FileExtension: `.${ext}`,
    Lcid,
    Format: ext.charAt(0).toUpperCase() + ext.slice(1),
  };
};

const CaptionEditor = (props) => {
  const {
    i18n,
    resourceRepository,
    videoId,
    captions,
    updateCaptions,
    errors,
    setErrors,
    block,
    setBlock,
    activityLogger,
  } = props;

  const [deleting, setDeleting] = React.useState(false);
  const [resIdToBeDelete, setResIdToBeDelete] = React.useState('');
  const [captionFileUploading, setCaptionFileUploading] = React.useState(null);

  const onCaptionRead = (file) => {
    setCaptionFileUploading(file);
    const caption = createCaptionFromFile(file, captions, i18n, setErrors);
    if (caption) {
      setBlock(true);
      setErrors([]);
      resourceRepository.createResource({
        file,
        onProgress: _.noop,
        fileExtension: caption.FileExtension,
        videoId,
        odataType: '#Model.VideoCaptionFile',
        resourceType: 'Caption',
        resourceParamters: {
          Lcid: caption.Lcid,
          Format: caption.Format,
        },
      })
        .then(() => {
          activityLogger.signal(`add caption: caption: ${JSON.stringify(caption)}`, 'video editor');
          return updateCaptions();
        })
        .catch(response => setErrors(FormatVideoError(response, i18n)))
        .finally(() => setBlock(false));
    }
  };

  const onCaptionDeleteClick = (id) => {
    setDeleting(true);
    setResIdToBeDelete(id);
    activityLogger.signal('delete caption', 'video editor');
  };

  const onDeleteCaption = () => {
    setDeleting(false);
    setBlock(true);
    setErrors([]);
    setCaptionFileUploading(null);
    resourceRepository.deleteResource({
      videoId,
      resourceId: resIdToBeDelete,
    })
      .then(() => updateCaptions())
      .catch(response => setErrors(FormatVideoError(response, i18n)))
      .finally(() => setBlock(false));
  };

  const onCloseDeleteConfirm = () => setDeleting(false);

  return (
    <div className="edit-container caption-editor">
      <div className="title">{i18n.getString('ImageExperience_EditCaptionTitle')}</div>
      <div className="edit-caption-description">
        {/* PLACEHOLDER */i18n.getString('ImageExperience_EditCaptionDescription')}
        <LearnMoreLink
          linkText={i18n.getString('Learn more')}
          topic=""
        />
      </div>
      <div className="caption-container">
        { block ?
          <Spinner size={SpinnerSize.large} label={i18n.getString('Loading')} /> :
          <React.Fragment>
            {
            (captions.length > 0 ? captions.map(cap => (
              <div key={`${cap.Name}-${cap.Lcid}`}>
                <Tag
                  text={C.LcidToFullName(cap.Lcid)}
                  onRemove={() => onCaptionDeleteClick(cap.Id)}
                />
              </div>)) :
            <div className="description">{i18n.getString('ImageExperience_NoCaptionFile')}</div>)
            }
            <CaptionPicker
              i18n={i18n}
              showErrors={setErrors}
              onFileRead={onCaptionRead}
              captionFile={captionFileUploading}
            />
          </React.Fragment>
      }
      </div>
      <Dialog
        hidden={!deleting}
        onDismiss={onCloseDeleteConfirm}
        dialogContentProps={{
          type: DialogType.normal,
          title: i18n.getString('ImageExperience_DeleteCaptionTitle'),
        }}
        modalProps={{ isBlocking: true, className: 'delete-confirm-dialog' }}
      >
        <div>{i18n.getString('ImageExperience_DeleteCaptionDescription')}</div>
        <DialogFooter>
          <PrimaryButton onClick={onDeleteCaption} text={i18n.getString('ImageExperience_Confirm')} />
          <DefaultButton onClick={onCloseDeleteConfirm} text={i18n.getString('ImageExperience_Cancel')} />
        </DialogFooter>
      </Dialog>
      { errors.map(error => (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline
          styles={{ root: { marginTop: 10 } }}
          key={error}
        >
          {error}
        </MessageBar>))
      }
    </div>);
};

CaptionEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  videoId: PropTypes.string.isRequired,
  resourceRepository: PropTypes.shape({
    createResource: PropTypes.func.isRequired,
    getResources: PropTypes.func.isRequired,
    deleteResource: PropTypes.func.isRequired,
  }).isRequired,
  captions: CaptionType,
  updateCaptions: PropTypes.func,
  errors: PropTypes.arrayOf(PropTypes.string),
  setErrors: PropTypes.func,
  block: PropTypes.bool,
  setBlock: PropTypes.func,
  activityLogger: PropTypes.shape({
    signal: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
  }),
};

CaptionEditor.defaultProps = {
  captions: [],
  updateCaptions: _.noop,
  errors: [],
  setErrors: _.noop,
  block: false,
  setBlock: _.noop,
  activityLogger: {
    signal: _.noop,
    error: _.noop,
  },
};
